import { classNames, Spacing, Title } from '@vkontakte/vkui';
import React, { type ReactElement, type ReactNode, memo, useCallback, useEffect, useRef } from 'react';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';

import styles from './WhatsNewDialog.css';

interface WhatsNewMobileDialogProps {
    primaryButtonText: ReactNode;
    primaryButtonStyle?: string;
    secondaryButtonText?: ReactNode;
    secondaryButtonStyle?: string;
    buttonSize?: ButtonSizeMode;
    title: string | ReactElement;
    text?: string | ReactElement;
    content?: ReactElement | string;
    imageAdditionalContent?: ReactElement;
    qaId?: string;
    imageUrl: string;
    showVideo?: boolean;
    videoUrl?: { webm?: string; mp4?: string };
    loopVideo?: boolean;
    hideImage?: boolean;
    videoWrapperClass?: string;
    paddingMode?: number;

    onShow?(): void;

    onClose?(): void;

    onClick?(): void;
    onSecondaryClick?(): void;
}

export const WhatsNewMobileDialog = memo<WhatsNewMobileDialogProps>(
    ({
        title,
        content,
        qaId,
        primaryButtonText,
        primaryButtonStyle,
        secondaryButtonText,
        secondaryButtonStyle,
        buttonSize = ButtonSizeMode.extraBig,
        imageUrl,
        showVideo,
        videoUrl,
        loopVideo,
        hideImage,
        videoWrapperClass,
        onClose,
        onClick,
        onSecondaryClick,
        onShow,
        imageAdditionalContent,
        paddingMode,
    }) => {
        const scrollRef = useRef<HTMLDivElement>(null);

        const handleClose = useCallback(() => {
            onClose?.();
        }, [onClose]);

        const handleOk = useCallback(() => {
            onClick?.();
            onClose?.();
        }, [onClose]);

        useEffect(() => {
            onShow?.();
        }, [onShow]);

        return (
            <MobileDialog
                id={qaId || 'whatsnew-mob'}
                onClose={handleClose}
                topmost
                closeOnDimmerClick
                scrollRef={scrollRef}
                mod="base"
                contextPadding="zero"
                closeMode="darkAndRound"
                isRebrandingQuotaLanding
            >
                <div ref={scrollRef} className={classNames(styles.rootMobile, { [styles[`padding${paddingMode}`]]: paddingMode })}>
                    {!hideImage && (
                        <div className={styles.imageContainer}>
                            {!imageAdditionalContent && imageUrl && <img src={imageUrl} className={styles.image} />}
                            {imageAdditionalContent}
                        </div>
                    )}
                    {showVideo && (
                        <div className={classNames(styles.imageContainer, videoWrapperClass)}>
                            <video loop={loopVideo} autoPlay muted playsInline className={styles.image}>
                                <source src={videoUrl?.webm} type="video/webm" />
                                {videoUrl?.mp4 && <source src={videoUrl?.mp4} type="video/mp4" />}
                            </video>
                        </div>
                    )}
                    <Spacing size={24} />
                    <Title level="1" className={styles.title}>
                        {title}
                    </Title>
                    <div className={styles.contentWrapper}>{content}</div>
                    <div className={classNames(styles.buttonWrapper, primaryButtonStyle)}>
                        <Button theme="vk" primary onClick={handleOk} sizeMode={buttonSize} data-test-id="primary">
                            {primaryButtonText}
                        </Button>
                    </div>
                    {secondaryButtonText && (
                        <div className={classNames(styles.buttonWrapper, secondaryButtonStyle)}>
                            <Button theme="vk" onClick={onSecondaryClick} sizeMode={buttonSize} data-test-id="secondary">
                                {secondaryButtonText}
                            </Button>
                        </div>
                    )}
                </div>
            </MobileDialog>
        );
    }
);

WhatsNewMobileDialog.displayName = 'WhatsNewMobileDialog';
